import { Link } from "react-router-dom";
import { Navbar } from "../../components/Navbar";
import { Footer } from "../../components/Footer";
import { SubprocessorTable } from "../../components/SubprocessorTable";

/**
 * Security Trust Centre.
 *
 * Known gaps — fill as they're resolved:
 * - SOC 2 target date (currently "in progress")
 * - Status page URL (status.intakall.io — not yet deployed)
 * - Key rotation cadence — currently documented as "at least annually,
 *   or after a suspected compromise", tighten once there's a formal policy.
 */
export default function SecurityTrustCentre() {
  return (
    <div className="min-h-screen bg-white font-body text-slate-800">
      <Navbar />
      <main>

      <article className="mx-auto max-w-3xl px-6 py-12 leading-relaxed">
        <header className="mb-10 border-b border-slate-200 pb-6">
          <h1 className="text-4xl font-semibold text-slate-900">Security Trust Centre</h1>
          <p className="mt-2 text-sm text-slate-500">
            Last updated: 2026-09-23
          </p>
          <div className="mt-6 rounded border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
            <p className="font-semibold">We do not train AI models on your data.</p>
            <p className="mt-1">
              Your meeting audio, transcripts, and messages are never used to train our
              models or our subprocessors'. On Pro and Enterprise, we also use each vendor's
              settings that keep your content out of their model training. On the Free plan, our speech-to-text provider, Deepgram, may use meeting audio to improve its models.
            </p>
          </div>
        </header>

        <Section n="1" title="Our posture">
          <p>
            Intakall is built by engineers, for engineers. We know what a serious security review
            looks like because we've been the ones asking those questions of our own vendors.
            This page is the short version of how we handle your data, where it goes, and who
            can read it.
          </p>
          <ul className="ml-6 mt-4 list-disc space-y-1">
            <li>Encryption at rest (AES-256) and in transit (TLS 1.3) everywhere.</li>
            <li>Per-user encryption of third-party integration tokens; keys held in host secrets.</li>
            <li>Row-Level Security enforced at the database layer so users can only read their team's data.</li>
            <li>Audit log of team actions (invites, integrations, billing changes) retained indefinitely.</li>
            <li>Responsible vulnerability disclosure policy with a 72-hour initial response SLA.</li>
          </ul>
        </Section>

        <Section n="2" title="Data flow during a meeting">
          <p>
            When you join a Google Meet with Intakall active, here's exactly what happens:
          </p>
          <ol className="ml-6 mt-4 list-decimal space-y-2">
            <li>
              Your browser captures the meeting audio (your microphone and the meeting tab) and
              streams it to our server over a secure WebSocket (WSS).
            </li>
            <li>
              Our server forwards the audio stream to Deepgram for real-time transcription.{" "}
              <strong>Audio is never written to disk</strong> — not on our servers, not in our
              databases.
            </li>
            <li>
              Deepgram returns transcript text. The transcript is stored in our Supabase database,
              scoped to your team, encrypted at rest.
            </li>
            <li>
              Recent transcript snippets are sent to OpenAI to extract topics. When the meeting
              ends, the full transcript is sent to OpenAI once to write the summary. We use vendor settings that exclude your data from model training.
            </li>
            <li>
              Extracted topics are matched against your connected integrations (GitHub, Linear, Jira, Slack) to
              produce sidebar cards. Integration tokens are stored encrypted per-user with a key
              that is never committed to source control.
            </li>
            <li>
              Cards are sent back to your browser over the same WebSocket and rendered in the
              sidebar. Nothing is shared outside your team.
            </li>
          </ol>
        </Section>

        <Section n="3" title="Subprocessors">
          <p>
            We use the following third parties to deliver the Service. Each has access only to
            the data necessary for their function. The canonical list, with change-notification
            policy, lives at{" "}
            <Link to="/subprocessors" className="text-indigo-600 hover:underline">
              /subprocessors
            </Link>
            .
          </p>
          <div className="mt-3 overflow-x-auto">
            <SubprocessorTable showData={false} />
          </div>
          <p className="mt-4 text-sm text-slate-600">
            See our{" "}
            <Link to="/privacy" className="text-indigo-600 hover:underline">
              Privacy Policy
            </Link>{" "}
            for the specific data each subprocessor receives.
          </p>
        </Section>

        <Section n="4" title="Encryption">
          <h3 className="mt-6 text-lg font-medium text-slate-900">4.1 At rest</h3>
          <ul className="ml-6 list-disc space-y-1">
            <li>
              Platform data (users, teams, meetings, transcripts) is stored in Supabase with
              AES-256 encryption at rest.
            </li>
            <li>
              Third-party integration tokens (GitHub, Linear) are additionally encrypted per-user
              with a secret master key held only in host-level secrets. A database dump alone is
              insufficient to use these tokens.
            </li>
          </ul>

          <h3 className="mt-6 text-lg font-medium text-slate-900">4.2 In transit</h3>
          <ul className="ml-6 list-disc space-y-1">
            <li>All client-to-server communication uses TLS 1.3.</li>
            <li>WebSocket connections use WSS.</li>
            <li>Server-to-subprocessor calls use TLS 1.3 over HTTPS.</li>
          </ul>

          <h3 className="mt-6 text-lg font-medium text-slate-900">4.3 Key management</h3>
          <ul className="ml-6 list-disc space-y-1">
            <li>The master encryption key lives only in our hosting provider's secret store.</li>
            <li>It is never checked into source control.</li>
            <li>Key rotation cadence: at least annually, or after a suspected compromise.</li>
          </ul>
        </Section>

        <Section n="5" title="Access controls">
          <ul className="ml-6 list-disc space-y-1">
            <li>
              <strong>Row-Level Security (RLS)</strong> enforced in Supabase — users can only
              read meetings, transcripts, and integrations belonging to their team.
            </li>
            <li>
              <strong>Least-privilege by default</strong> — integration tokens are scoped to read
              the data you explicitly grant during OAuth. We never request write scopes we don't
              need.
            </li>
            <li>
              <strong>Rate limiting</strong> on sensitive endpoints (billing checkout, invite
              creation, invite acceptance) to reduce abuse and enumeration risk.
            </li>
          </ul>
        </Section>

        <Section n="6" title="Compliance">
          <p>
            We are pre-launch and do not yet hold formal compliance attestations. Here is where
            we are and where we're heading:
          </p>
          <ul className="ml-6 mt-3 list-disc space-y-1">
            <li>
              <strong>SOC 2 Type I:</strong> in progress. We are building controls to the SOC 2
              framework from day one so the formal audit is a formality rather than a refactor.
            </li>
            <li>
              <strong>SOC 2 Type II:</strong> planned 12 months after Type I.
            </li>
            <li>
              <strong>GDPR:</strong> we honour data-subject rights (access, deletion,
              portability) today. See our{" "}
              <Link to="/privacy" className="text-indigo-600 hover:underline">
                Privacy Policy
              </Link>
              .
            </li>
            <li>
              <strong>DPA:</strong> available on request for B2B customers. Email{" "}
              <a href="mailto:security@intakall.io" className="text-indigo-600 hover:underline">
                security@intakall.io
              </a>
              .
            </li>
          </ul>
        </Section>

        <Section n="7" title="Vulnerability disclosure">
          <p id="vulnerability-disclosure">
            We welcome reports from security researchers. If you believe you've found a
            vulnerability in Intakall, please email{" "}
            <a href="mailto:security@intakall.io" className="text-indigo-600 hover:underline">
              security@intakall.io
            </a>
            {" "}with a detailed description and (where possible) reproduction steps.
          </p>
          <ul className="ml-6 mt-3 list-disc space-y-1">
            <li>We will acknowledge your report within 72 hours.</li>
            <li>We do not pursue legal action against good-faith researchers who follow this policy.</li>
            <li>
              Please do not access data beyond what is necessary to demonstrate the issue, do not
              publicly disclose until we have fixed it, and do not use automated scanners against
              production.
            </li>
          </ul>
        </Section>

        <Section n="8" title="Incident response">
          <ul className="ml-6 list-disc space-y-1">
            <li>
              We notify affected users by email within <strong>72 hours</strong> of becoming aware
              of a breach that materially affects their data, and the relevant supervisory
              authority in the same window.
            </li>
            <li>
              Post-incident reports are published to this page for material incidents, including
              timeline, impact, root cause, and remediation.
            </li>
            <li>Public status page: coming soon.</li>
          </ul>
        </Section>

        <Section n="9" title="Known limitations (current state)">
          <p>Because we are pre-launch, there are things we are not yet doing. Being honest about them:</p>
          <ul className="ml-6 mt-3 list-disc space-y-1">
            <li>No SOC 2 attestation yet (see Section 6).</li>
            <li>No bug bounty programme — reports are welcomed but not paid today.</li>
            <li>Formal pen test coverage is limited; we rely on dependency scanning and code review.</li>
            <li>Status page not yet published.</li>
          </ul>
          <p className="mt-4">
            If any of these are blockers for your team, email us — we've likely heard it before
            and can tell you our plan.
          </p>
        </Section>

        <Section n="10" title="Contact">
          <ul className="ml-6 list-disc space-y-1">
            <li>
              <strong>Security issues:</strong>{" "}
              <a href="mailto:security@intakall.io" className="text-indigo-600 hover:underline">
                security@intakall.io
              </a>
            </li>
            <li>
              <strong>Privacy questions:</strong>{" "}
              <a href="mailto:privacy@intakall.io" className="text-indigo-600 hover:underline">
                privacy@intakall.io
              </a>
            </li>
            <li>
              <strong>Full Privacy Policy:</strong>{" "}
              <Link to="/privacy" className="text-indigo-600 hover:underline">
                /privacy
              </Link>
            </li>
          </ul>
        </Section>

        <footer className="mt-16 border-t border-slate-200 pt-6 text-sm text-slate-500">
          <Link to="/" className="text-indigo-600 hover:underline">
            ← Back to Intakall
          </Link>
        </footer>
      </article>
      </main>
      <Footer />
    </div>
  );
}

function Section({ n, title, children }: { n: string; title: string; children: React.ReactNode }) {
  return (
    <section className="mt-10">
      <h2 className="text-2xl font-semibold text-slate-900">
        <span className="mr-3 text-slate-400">{n}.</span>
        {title}
      </h2>
      <div className="mt-3 space-y-3">{children}</div>
    </section>
  );
}


